import { QuestionSetModel } from './questionType';
import { BookUnitModel } from './bookTypes';
/* 국어 분석지문 및 지문 독해 모델 */
// 1. 독서(비문학) 문제 유형
export const READING_QUESTION_TYPES = [
    '세부내용_일치불일치',  // 지문 정보의 사실적 이해
    '전개방식_구조파악',    // 글 전체의 전개 논리 및 단락 간 관계
    '핵심개념_원리이해',    // 핵심 개념, 원리, 비례 관계 이해
    '사례적용_보기문항',    // <보기>의 새로운 상황/데이터에 원리 적용 (고난도)
    '논지_반박_어휘',       // 어휘의 문맥적 의미, 빈칸 추론, 논지 비판
] as const;

// 2. 문학 문제 유형
export const LITERATURE_QUESTION_TYPES = [
    '표현상특징_서술상특징', // [내재적] 운율, 비율, 시상전개, 시점 등
    '시어_소재_의미파악',   // [내재적] 시어/소재의 함축적 의미 및 대립 구조
    '화자_서술자_정서태도', // [내재적] 화자의 상황, 정서, 태도, 인물 갈등
    '외재적관점_보기감상',   // [외재적] <보기>(시대, 작가, 독자)를 바탕으로 한 감상 (고난도)
    '작품간_공통점_차이점', // 2개 이상 지문 복합 비교
] as const;

// 3. 화법과 작문(화작) 문제 유형
export const SPEECH_WRITING_QUESTION_TYPES = [
    '말하기전략_발화특징',  // 대화/연설자의 말하기 방식 및 전략
    '자료활용_조조건수용',  // <보기> 자료/조건을 활용한 글쓰기 방안
    '초고수정_고쳐쓰기',    // 문장 호응, 어휘 적절성, 내용 추가/삭제
] as const;

// 4. 언어와 매체(언매) 문제 유형
export const LANGUAGE_MEDIA_QUESTION_TYPES = [
    '문법_개념적용',        // 음운, 단어(품사/사형성), 문장, 담화/국어사
    '문법_지문형복합문항',  // 긴 문법 지문을 읽고 적용하는 문항 (14~15번)
    '매체_수용_생산'        // 뉴미디어, 블로그, 방송 등 매체 수용 태도 및 정보 판별
] as const;

// 유니온 타입 추출
export type ReadingQuestionType = (typeof READING_QUESTION_TYPES)[number];
export type LiteratureQuestionType = (typeof LITERATURE_QUESTION_TYPES)[number];
export type SpeechWritingQuestionType = (typeof SPEECH_WRITING_QUESTION_TYPES)[number];
export type LanguageMediaQuestionType = (typeof LANGUAGE_MEDIA_QUESTION_TYPES)[number];

export type KoreanQuestionType =
    | ReadingQuestionType
    | LiteratureQuestionType
    | SpeechWritingQuestionType
    | LanguageMediaQuestionType;

// 국어분류 
export const KOREAN_CATEGORIES = [
    '독서',
    '문학',
    '화작',
    '언매',
] as const;

export type KoreanCategory = (typeof KOREAN_CATEGORIES)[number];

// 1. 독서(비문학) 매핑 구조
export const READING_BRANCHES = {
    '인문/예술': ['철학', '역사', '윤리', '미학', '미술', '음악'],
    '사회/문화': ['법률', '경제', '정치', '사회', '언어'],
    '과학/기술': ['물리', '화학', '생명과학', '지구과학', '컴퓨터/IT', '공학'],
    '주제통합': ['1주제, 2입장'],
    '독서이론': ['독서의 본질, 방법, 태도의 짧은지문']
} as const;

// 2. 문학 매핑 구조
export const LITERATURE_BRANCHES = {
    '서정 갈래 (시)': ['고전시가', '현대시'],
    '서사 갈래': ['고전소설', '현대소설'],
    '극 갈래': ['고전극', '현대극'],
    '교술 갈래': ['고전수필', '현대수필']
} as const;

// 3. 화법과 작문(화작) 매핑 구조
export const SPEECH_WRITING_BRANCHES = {
    '화법': ['대화', '발표', '연설', '토의', '협상'],
    '작문': ['설명', '설득', '감상'],
    '융합': []
} as const;

// 4. 언어와 매체(언매) 매핑 구조
export const LANGUAGE_MEDIA_BRANCHES = {
    '언어(문법)': ['음운론', '단어론', '문장론', '담화론', '국어사(고전문법)'],
    '매체': ['인쇄/방송 매체', '디지털/인터넷 매체']
} as const;

// 중분류 타입 정의
export type ReadingBranch = keyof typeof READING_BRANCHES;
export type LiteratureBranch = keyof typeof LITERATURE_BRANCHES;
export type SpeechWritingBranch = keyof typeof SPEECH_WRITING_BRANCHES;
export type LanguageMediaBranch = keyof typeof LANGUAGE_MEDIA_BRANCHES;

export type KoreanBranch =
    | ReadingBranch
    | LiteratureBranch
    | SpeechWritingBranch
    | LanguageMediaBranch;

// 소분류/장르 타입 정의
export type ReadingGenre = typeof READING_BRANCHES[ReadingBranch][number];
export type LiteratureGenre = typeof LITERATURE_BRANCHES[LiteratureBranch][number];
export type SpeechWritingGenre = typeof SPEECH_WRITING_BRANCHES[SpeechWritingBranch][number];
export type LanguageMediaGenre = typeof LANGUAGE_MEDIA_BRANCHES[LanguageMediaBranch][number];

export type KoreanGenre =
    | ReadingGenre
    | LiteratureGenre
    | SpeechWritingGenre
    | LanguageMediaGenre;

// 관점 정의
export const PERSPECTIVE_CATEGORIES = ['내재적', '외재적'] as const;
export const PERSPECTIVE_TYPES = ['절대주의적', '표현론적', '반영론적', '효용론적'] as const;

// 표현 및 서술상의 특징 분류 구조 정의 (문학 표현 / 독서 전개 방식 통합)
export const EXPRESSION_FEATURE_TAXONOMY = {
    // 1. 문학(시/소설 등) 표현상의 특징
    '비유 및 상징': [
        '직유법',
        '은유법',
        '의인법',
        '활유법(의물화)',
        '대유법',
        '객관적 상관물',
        '감정이입',
        '개인적 상징',
        '관습적 상징',
        '원형적 상징'
    ],
    '감각 및 심상': [
        '시각적 심상',
        '청각적 심상',
        '촉각적 심상',
        '후각적 심상',
        '미각적 심상',
        '공감각적 심상 (감각의 전이)',
        '동적 심상',
        '정적 심상',
        '색채 대비',
        '명암 대비',
        '감각적 대비'
    ],
    '운율 및 반복': [
        '음보율',
        '음수율',
        '3음보율',
        '4음보율',
        '통사 구조의 반복 (대구)',
        '시어의 반복',
        '시구의 반복',
        '수미상관',
        '의성어',
        '의태어'
    ],
    '수사 및 어조': [
        '반어법',
        '역설법',
        '설의법',
        '영탄법',
        '풍자',
        '해학',
        '희화화',
        '경어체(존댓말 어조)',
        '평서체(단정적 어조)',
        '어조의 전환'
    ],
    '시상 전개 방식': [
        '시간의 흐름에 따른 전개',
        '공간의 이동에 따른 전개',
        '시선의 이동에 따른 전개',
        '선경후정',
        '과거와 현재의 대비',
        '이상과 현실의 대비',
        '점층적 전개',
        '연쇄적 전개'
    ],
    '서사 / 극 특수 서술 기법': [
        '서술자의 개입 (편집자적 논평)',
        '요약적 제시 (말하기)',
        '극적 제시 (보여주기)',
        '장면의 전환',
        '액자식 구성',
        '의식의 흐름',
        '몽유록 구조'
    ],

    // 2. 독서(비문학) 지문 전개 방식
    '내용 설명 방식': [
        '정의 (개념 규정)',
        '지정 (개념 명시)',
        '예시 (사례 제시)',
        '인용 (전문가/문헌)',
        '비교 (공통점 제시)',
        '대조 (차이점 제시)',
        '분류 (유형 구분)',
        '분석 (구조/구성요소 해체)'
    ],
    '논지 전개 구조': [
        '인과 (원인과 결과)',
        '문제와 해결 (Problem & Solution)',
        '과정 및 단계 (순서적 절차)',
        '통시적 전개 (역사적/시간적 흐름)'
    ],
    '논증 및 설득 기법': [
        '연역 논증',
        '귀납 논증',
        '유추 (유사성 기반 추론)',
        '대립적 입장 제시',
        '반론 및 재반론',
        '예상되는 반론 고려'
    ],
    '제재별 특수 서술 방식': [
        '과학·기술 원리 설명',
        '과학·기술 구조 및 기능 분석',
        '과학·기술 시각 자료(도해/그래프) 활용',
        '경제·법률 제도/법률 적용',
        '경제·법률 변수 간 상호작용 분석',
        '경제·법률 조건 분석',
        '인문·철학 관점 비교',
        '인문·철학 사상의 변천 과정',
        '인문·철학 대립적 견해 분석'
    ]
} as const;

export type PerspectiveCategory = (typeof PERSPECTIVE_CATEGORIES)[number];
export type PerspectiveType = (typeof PERSPECTIVE_TYPES)[number];
export type ExpressionFeatureType = keyof typeof EXPRESSION_FEATURE_TAXONOMY;
export type ExpressionFeatureConcept = typeof EXPRESSION_FEATURE_TAXONOMY[ExpressionFeatureType][number];

// 화자/서술자 분석 모델
export interface SpeakerAnalysisModel {
    /** 화자/서술자의 위치 및 시점 (예: '1인칭 관찰자 시점', '표면에 드러난 화자') */
    pointOfView: string;
    /** 화자가 처한 상황 */
    situation: string;
    /** 화자/서술자의 핵심 정서 (예: '그리움', '상실감') */
    emotion: string;
    /** 화자/서술자의 태도 (예: '의지적', '비판적', '순응적') */
    attitude: string;
}



// 관점 분석 모델
export type PerspectiveModel =
    | {
        category: '내재적';
        subtype: '절대주의적';
        targetEntity: string;
        textSegment: string;
        description: string;
    }
    | {
        category: '외재적';
        subtype: '표현론적' | '반영론적' | '효용론적';
        targetEntity: string;
        textSegment: string;
        description: string;
    };



/* 독해 및 공통 모델 */
export interface KoreanParagraphModel {
    flow: string; // 단락흐름 (예: [1] 구체적 통계 수치와 현황 제시를 통한 독자의 주의 환기 및 문제 상황 도출)
    title: string; // 단락제목 (예: 1문단)
    korean: string; // 단락 내용
    chunks?: KoreanChunkModel[]; // 중요부분
    expressionFeatures?: ExpressionFeatureModel[]; // 표현상의 특징 분석

}

// 표현상의 특징 모델
export interface ExpressionFeatureModel {
    type: ExpressionFeatureType;
    concept: ExpressionFeatureConcept;      // 상세 세부 문학 개념명
    textSegment: string;  // 해당 기법이 쓰인 구절
    effect: string;       // 표현상의 효과 (예: '시적 긴장감 형성', '상징성 강화')
}

export interface KoreanChunkModel {
    chunk: string; // 중요부분
    summary: string; // 구절의 짧은 의미 요약 (인라인 밑줄 아래 표시용, 최대 20자 내외)
    description: string; // 수능 출제 선지화 포인트 해설 (하단 출제포인트 표시용)
    words: KoreanWordModel[]; // 문장안의 단어 리스트
}

export interface KoreanWordModel {
    word: string; // 단어
    meaning: string; // 의미
}

export interface KoreanStructureModel {
    title: string; // 소제목
    summary: string; // 요약
}

// 최종 통합 모델 (최상위 국어 지문/페이지 분석 모델)
export interface KoreanTextModel extends BookUnitModel {
    // 원문 정보
    korean?: string; // 지문 원문 텍스트

    // 분류 정보
    category?: KoreanCategory; // 대분류 (예: '독서', '문학', '화작', '언매')
    branch?: KoreanBranch; // 중분류 (예: '인문/예술', '서정 갈래 (시)', '화법', '언어(문법)' 등)
    genre?: KoreanGenre; // 소분류 (예: '현대시', '고전소설', '과학', '경제' 등 상세 갈래)
    themes?: string[]; // 테마 (지문이 관통하는 핵심 제재 및 주제적 키워드 목록)

    // 분석정보 
    gist?: string; // 핵심 요지 (지문의 핵심 메시지/창작 의도 1줄 요약)
    summary?: string; // 전체 요약 (지문 전체 내용을 1~2문장으로 압축 요약한 설명글)
    textType?: string[]; // 글의 종류 (예: ['자유시', '서정시'] 또는 ['설명문', '독서'])
    textFeatures?: string[]; // 글의 특징 (글의 표현상 특징 및 내용 설명 방식 특징 목록)
    textStyle?: string; // 글의 성격 (예: '성찰적, 감각적, 주관적')
    backgroundKnowledge?: string[]; // 배경지식 (지문 소재와 관련된 배경지식 설명 목록)
    questionTypes?: string[]; // 출제 예상 유형 (수능/내신 출제 예상 유형 목록)
    paragraphs?: KoreanParagraphModel[]; // 단락분석 배열 (각 연/문단별 상세 분석 데이터 목록)

    // 독해/비문학 (선택적)
    structures?: KoreanStructureModel[]; // 글의 구성 (서론, 본론, 결론 등)

    // 문학작품 분석 (선택적)
    /** 1. 화자 / 서술자 분석 */
    speaker?: SpeakerAnalysisModel;

    /** 2. 감상의 관점 분석 */
    perspectives?: PerspectiveModel[];
    parentPage?: number;

    // DB 완료 현황 스태츠
    _stats?: {
        conceptDone: boolean;
        analysisDone: boolean;
        questionsCount: number;
        updatedAt: number;
    };

    // AI 생성 데이터들
    concepts?: any[]; // 개념설명 세션
    questionSetIds?: string[]; // 연결된 문항 세트(bookQuestionSets) ID 리스트


}

export interface BookQuestionSetModel {
    id: string; // docId: bookId_page_pageNumber_set_timestamp
    paragraphId: string; // 부모 지문 카드 ID
    subject: string;
    passage?: string; // 지문 원문
    questionIds: string[]; // 소속 개별 문항 ID 리스트 (bookQuestions 컬렉션 참조)
    createdAt: number;
}



export const getPageNumberFromId = (id: string): number => {
    const parts = id.split('_page_');
    return parts.length > 1 ? parseInt(parts[1], 10) || 0 : 0;
};
